import React, { useMemo } from "react";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ReferenceLine, ResponsiveContainer,
} from "recharts";

function phiPct(t, elution) {
  if (!elution) return null;
  if (elution.mode !== "gradient") return elution.percent_organic ?? null;
  const g = elution.gradient || {};
  const start = g.start_percent ?? 10;
  const end = g.end_percent ?? 90;
  const dur = Math.max(0.1, g.duration_min ?? 10);
  return start + (end - start) * Math.min(1, Math.max(0, t / dur));
}

/**
 * Live / static chromatogram plot with optional %organic gradient overlay.
 * props: trace [{t,mAU}], cursor, runtime, peaks [{name,tR}], done, elution {mode,percent_organic,gradient}
 */
export default function Chromatogram({ trace = [], cursor = 0, runtime = 10, peaks = [], done = false, elution = null, height = 340 }) {
  const visible = useMemo(() => trace.filter((p) => p.t <= cursor + 1e-6), [trace, cursor]);

  const data = useMemo(
    () => visible.map((p) => ({ ...p, pct: phiPct(p.t, elution) })),
    [visible, elution]
  );

  const yMax = useMemo(() => Math.ceil(Math.max(10, ...trace.map((p) => p.mAU)) * 1.15), [trace]);
  const showGradient = !!elution;

  return (
    <div className="instrument-surface rounded-md p-3 grid-overlay" data-testid="chromatogram-plot">
      <div style={{ width: "100%", height }}>
        <ResponsiveContainer>
          <LineChart data={data} margin={{ top: 10, right: showGradient ? 42 : 16, left: -6, bottom: 4 }}>
            <CartesianGrid stroke="#27272a" strokeDasharray="3 3" vertical={false} />
            <XAxis
              type="number"
              dataKey="t"
              domain={[0, runtime]}
              tick={{ fill: "#a1a1aa", fontSize: 11, fontFamily: "JetBrains Mono" }}
              stroke="#3f3f46"
              tickCount={8}
              label={{ value: "Waktu (menit)", position: "insideBottom", offset: -2, fill: "#71717a", fontSize: 11 }}
            />
            <YAxis
              yAxisId="left"
              domain={[(dataMin) => Math.min(0, dataMin), yMax]}
              tick={{ fill: "#a1a1aa", fontSize: 11, fontFamily: "JetBrains Mono" }}
              stroke="#3f3f46"
              width={46}
              label={{ value: "mAU", angle: -90, position: "insideLeft", fill: "#71717a", fontSize: 11 }}
            />
            {showGradient && (
              <YAxis
                yAxisId="right"
                orientation="right"
                domain={[0, 100]}
                tick={{ fill: "#f59e0b", fontSize: 11, fontFamily: "JetBrains Mono" }}
                stroke="#7c5e12"
                width={40}
                label={{ value: "%Org", angle: 90, position: "insideRight", fill: "#b45309", fontSize: 11 }}
              />
            )}
            <Tooltip
              contentStyle={{ background: "#18181b", border: "1px solid #27272a", borderRadius: 6, color: "#fafafa", fontFamily: "JetBrains Mono", fontSize: 12 }}
              labelFormatter={(v) => `t = ${Number(v).toFixed(2)} mnt`}
              formatter={(v, name) => (name === "pct" ? [`${Number(v).toFixed(0)} %`, "%Organik"] : [`${Number(v).toFixed(2)} mAU`, "Sinyal"])}
            />
            <Line yAxisId="left" type="monotone" dataKey="mAU" stroke="#10B981" strokeWidth={1.8} dot={false} isAnimationActive={false} />
            {showGradient && (
              <Line yAxisId="right" type="monotone" dataKey="pct" stroke="#f59e0b" strokeWidth={1.5} strokeDasharray="5 4" dot={false} isAnimationActive={false} />
            )}
            {cursor < runtime && cursor > 0 && (
              <ReferenceLine yAxisId="left" x={cursor} stroke="#EF4444" strokeWidth={1.5} />
            )}
            {done &&
              peaks.map((p) => (
                <ReferenceLine
                  key={p.name}
                  yAxisId="left"
                  x={p.tR}
                  stroke="#0ea5e9"
                  strokeDasharray="2 2"
                  label={{ value: p.name, position: "top", fill: "#38bdf8", fontSize: 10, fontFamily: "JetBrains Mono" }}
                />
              ))}
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
